import { TFilterField, TFilterValue } from "@irohana-dev/components";
import { TOption } from "@/types/common";
import { applyColumnSettings as applyKitColumnSettings, asStringArray, buildColumnOptions, fromDateRangeValue } from "@/components/commons/filter-condition/filter-condition-adapter-kit";
import { JOB_COUNT_OPTIONS } from "@/features/company/constants/common";
import {
  COMPANY_MULTI_SELECT_FILTER_KEYS,
  TCompanyDisplayColumnSetting,
  TCompanyFilterFormExtended,
  defaultCompanyDisplayColumns,
  defaultCompanyFilterValues,
} from "@/features/company/components/company-list/company-filter-shared";

const DATE_RANGE_KEY = "registrationDate";

type TCompanyFilterOptions = {
  managementNumber: TOption[];
  companyName: TOption[];
  industry: TOption[];
  prefecture: TOption[];
  contactPerson: TOption[];
};

/**
 * Field config for the shared FilterCondition panel. `city` derives its options and its
 * disabled state from the prefectures chosen so far, so the panel needs no notion of one
 * field depending on another.
 */
export const buildCompanyFilterFields = (
  options: TCompanyFilterOptions,
  cityOptions: TOption[],
  hasPrefecture: boolean,
): TFilterField[] => [
  { type: "text", key: "managementNumber", label: "企業管理番号", placeholder: "入力してください" },
  { type: "multiSelect", key: "companyName", label: "企業名", options: options.companyName },
  { type: "multiSelect", key: "companySpecificIndustryId", label: "分野", options: options.industry },
  { type: "multiSelect", key: "prefecture", label: "所在地（都道府県）", options: options.prefecture },
  { type: "multiSelect", key: "city", label: "所在地（市区町村）", options: cityOptions, disabled: !hasPrefecture },
  { type: "text", key: "numberOfEmployees", label: "従業員数", placeholder: "入力してください" },
  { type: "multiSelect", key: "contactPersonName", label: "担当者", options: options.contactPerson },
  { type: "multiSelect", key: "jobCount", label: "求人数", options: JOB_COUNT_OPTIONS },
  { type: "dateRange", key: DATE_RANGE_KEY, label: "登録日" },
  { type: "checkbox", key: "isShowCompanyArchive", label: "アーカイブから検索" },
];

/** Columns are not 1:1 with fields — `createdAt` has no filter, `city` has no column. */
export const buildCompanyColumnOptions = buildColumnOptions;

/** The panel keeps a single `registrationDate` range; the form keeps two separate keys. */
export const toFilterValue = (filters: TCompanyFilterFormExtended): TFilterValue => {
  const value: TFilterValue = {};
  for (const [key, raw] of Object.entries(filters)) {
    if (key === "registrationDateFrom" || key === "registrationDateTo") continue;
    if (key === "isShowCompanyArchive") {
      if (raw) value[key] = true;
      continue;
    }
    if (Array.isArray(raw)) {
      if (raw.length > 0) value[key] = raw.map(String);
      continue;
    }
    if (raw === "" || raw === null || raw === undefined) continue;
    // Conditions saved before these fields became multi-select hold a scalar. The grid reads
    // a non-array as empty, so leaving it through would show a blank control while the stale
    // value stayed in the filter and kept reaching the query.
    if (COMPANY_MULTI_SELECT_FILTER_KEYS.includes(key as keyof TCompanyFilterFormExtended)) {
      value[key] = [String(raw)];
      continue;
    }
    value[key] = raw as string;
  }
  const { registrationDateFrom, registrationDateTo } = filters;
  if (registrationDateFrom || registrationDateTo) {
    value[DATE_RANGE_KEY] = { from: registrationDateFrom, to: registrationDateTo };
  }
  return value;
};

export const fromFilterValue = (value: TFilterValue): TCompanyFilterFormExtended => {
  const range = fromDateRangeValue(value, DATE_RANGE_KEY);
  return {
    ...defaultCompanyFilterValues,
    managementNumber: (value.managementNumber as string) ?? "",
    companyName: asStringArray(value.companyName),
    companySpecificIndustryId: asStringArray(value.companySpecificIndustryId),
    prefecture: asStringArray(value.prefecture),
    city: asStringArray(value.city),
    numberOfEmployees: (value.numberOfEmployees as string) ?? "",
    contactPersonName: asStringArray(value.contactPersonName),
    jobCount: asStringArray(value.jobCount),
    registrationDateFrom: range.from,
    registrationDateTo: range.to,
    isShowCompanyArchive: value.isShowCompanyArchive === true ? 1 : 0,
  };
};

/**
 * Column order/visibility as the panel expresses it, mapped back onto the stored settings.
 * Columns the panel omits keep their previous position, appended in their original order.
 */
export const applyColumnSettings = (columns: TCompanyDisplayColumnSetting[], order: string[], hidden: string[]): TCompanyDisplayColumnSetting[] =>
  applyKitColumnSettings(columns, order, hidden, defaultCompanyDisplayColumns);
